import { floor } from "mathjs";
import { type AxiosResponse } from "axios";
import proj4 from "proj4";
import { LatLng } from "leaflet";

// 丸文字を定義。
// 丸文字の参考URL: https://ja.wikipedia.org/wiki/%E4%B8%B8%E5%8D%B0#%E7%AC%A6%E5%8F%B7%E4%BD%8D%E7%BD%AE
const REGEX_CIRCLES = /[⃘⃝○◌◍●◙◯⚪⚫⚬❍⬤⭘￮OＯ]/gu;

const stringBooleanToBoolean = (args: "true" | "false"): boolean => {
  return JSON.parse(args.toLowerCase()) as boolean;
};

const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, (s) => "_" + s.charAt(0).toLowerCase());
};

const numberFormat = (
  num: number,
  options?: Intl.NumberFormatOptions,
  roundingMode?: "floor"
): string => {
  let value = num;
  if (options?.maximumFractionDigits != null && roundingMode === "floor") {
    value = floor(num, options.maximumFractionDigits);
  }
  return new Intl.NumberFormat("ja-JP", options).format(value);
};

// 2つのDateオブジェクトが同一日か判定する
const isSameDate = (a: Date, b: Date): boolean => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

/**
 * 半角数字とハイフンを全角文字に変換する。それ以外の文字はそのまま
 * @param value 変換したい文字列
 * @return 変換後の文字列
 */
const NYHalfWidthNumberToFillWidthNumber = (value: string): string => {
  const regexNumber = /[0-9]/g;
  const regexHyphen =
    /[-\uFF0D\uFE63\u2212\u2010\u2043\u2011\u2012\u2013\u2014\ufe58\u2015\u23AF\u23E4\u02D7\u2796\u208B\u30FC\uFF70]/g;

  // 一文字ごとに分割して配列に変換
  const stringArray = [...value];

  // 一文字ずつ実行
  const result = stringArray.map((c) => {
    // 数字であれば全角に変換
    c = c.replace(regexNumber, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
    });

    // ハイフンであれば「KATAKANA-HIRAGANA PROLONGED SOUND MARK」(utf8: e3 83 bc)に置き換える
    c = c.replace(regexHyphen, "ー");

    return c;
  });

  // 文字列に戻す
  return result.join("");
};

/**
 * 全角数字とハイフンを半角文字に変換する。それ以外の文字はそのまま
 * NYHalfWidthNumberToFillWidthNumberの逆関数
 * @param value 変換したい文字列
 * @return 変換後の文字列
 */
const NYFillWidthNumberToHalfWidthNumber = (value: string): string => {
  const regexNumber = /[０-９]/g;
  const regexHyphen =
    /[ー\uFF0D\uFE63\u2212\u2010\u2043\u2011\u2012\u2013\u2014\ufe58\u2015\u23AF\u23E4\u02D7\u2796\u208B\uFF70]/g;

  return value
    .replace(regexNumber, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
    })
    .replace(regexHyphen, "-");
};

// 日本の祝日CSVデータ。内閣府WebサイトからCSVを取得し、フォーマット等変換せずコピペしています。
// スクリプトでCSVを分解し必要なデータを取り出しているため、そのままコピペで問題ありません。
// 2023年以前は不要なので削除しています。
// 2025年分は2024年2月に掲載されますので、2025年になる前に反映してください。
// https://www8.cao.go.jp/chosei/shukujitsu/gaiyou.html
const nationalHolidayCsv = `
2024/1/1,元日
2024/1/8,成人の日
2024/2/11,建国記念の日
2024/2/12,休日
2024/2/23,天皇誕生日
2024/3/20,春分の日
2024/4/29,昭和の日
2024/5/3,憲法記念日
2024/5/4,みどりの日
2024/5/5,こどもの日
2024/5/6,休日
2024/7/15,海の日
2024/8/11,山の日
2024/8/12,休日
2024/9/16,敬老の日
2024/9/22,秋分の日
2024/9/23,休日
2024/10/14,スポーツの日
2024/11/3,文化の日
2024/11/4,休日
2024/11/23,勤労感謝の日
2025/1/1,元日
2025/1/13,成人の日
2025/2/11,建国記念の日
2025/2/23,天皇誕生日
2025/2/24,休日
2025/3/20,春分の日
2025/4/29,昭和の日
2025/5/3,憲法記念日
2025/5/4,みどりの日
2025/5/5,こどもの日
2025/5/6,休日
2025/7/21,海の日
2025/8/11,山の日
2025/9/15,敬老の日
2025/9/23,秋分の日
2025/10/13,スポーツの日
2025/11/3,文化の日
2025/11/23,勤労感謝の日
2025/11/24,休日
`;

// テキストボックスにおいて、数値以外の文字を含む値のペーストを抑制する
// 特に-+eEの入力を防止したい
const preventNonNumericValueWhenPaste = (
  event: React.ClipboardEvent<HTMLDivElement> | undefined
): void => {
  const pasted = event?.clipboardData?.getData("text") ?? "";
  const re = /\D/; // 半角数値以外の全ての文字
  if (re.test(pasted)) {
    event?.preventDefault();
  }
};

// テキストボックスにおいてeE-+のキー入力を抑止する
const preventNonNumericValueWhenKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>
): void => {
  if (
    event.key === "e" ||
    event.key === "E" ||
    event.key === "-" ||
    event.key === "+"
  ) {
    event.preventDefault();
  }
};

// テキストボックスにおいて数値以外のキー入力を抑止する。バックスペースを除く
const preventNonNumericValueWithoutBackspaceWhenKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>
): void => {
  // バックスペースはOK
  if (event.key === "Backspace") {
    return;
  }
  const re = /\D/; // 半角数値以外の全ての文字
  if (re.test(event.key)) {
    event?.preventDefault();
  }
};

/**
 * スペースキーを抑止する
 * @param event キーボードイベント
 * @returns void
 */
const preventSpaceKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
  if (event.key === " " || event.key === "Space") {
    event.preventDefault();
  }
};

/**
 * 配列文字列をクリーンにする
 * - trim()&空白＆重複削除
 * @param arrayVal 入力値
 * @returns 変換後の値
 */
const handleCleanArrayValue = (arrayVal: string[]): string[] => {
  return Array.from(
    new Set(arrayVal.map((v) => v.trim()).filter((v) => v !== ""))
  );
};

/**
 * INPUT専用の区切り文字を定義
 * - value: 区切り文字
 * - label: 区切り文字の日本語表記
 */
const InputDelimiters = [
  { value: ",", label: "半角カンマ「,」" },
  { value: "，", label: "全角カンマ「，」" },
  { value: "、", label: "テン「、」" },
  { value: "\t", label: "タブ" },
  { value: "\n", label: "改行" },
];

/**
 * INPUT専用の区切り文字で分割するための正規表現
 */
const INPUT_REGEX_SPLIT = new RegExp(
  `[${InputDelimiters.map((delimiter) => delimiter.value).join("")}]`
);

/**
 * INPUT専用の区切り文字を日本語で返す
 * - 「、」で区切って連結
 * - labelが同じ場合の下記例のように連結
 *   - 読点「,」「、」
 * @returns 連結された区切り文字の日本語表記
 */
const getInputDelimiterJapanese = (): string => {
  // setで重複を削除
  const uniqueLabels = new Set(
    InputDelimiters.map((delimiter) => delimiter.label)
  );
  // 表示用のラベル一覧
  const displayLabels = Array.from(uniqueLabels).map((label) => {
    const filteredDelimiters = InputDelimiters.filter(
      (delimiters) => delimiters.label === label
    );
    if (filteredDelimiters.length > 1) {
      return `${label}${filteredDelimiters
        .map((l) => `「${l.value}」`)
        .join("")}`;
    }
    return filteredDelimiters[0].label;
  });
  // 「、」で区切って連結
  return displayLabels.join("、");
};

/**
 * INPUT専用
 * 区切り文字で分割する
 * - trimして空白除外もしてます
 * @param pastedText 入力値
 * @returns 変換後の値
 */
const handleSplitInputValues = (pastedText: string): string[] => {
  return pastedText
    .split(INPUT_REGEX_SPLIT)
    .map((value) => value.trim())
    .filter((value) => value !== "");
};

const getAttachedFileNameFromHeader = (
  res: AxiosResponse,
  fallBackName: string
): string => {
  let filename = fallBackName; // ヘッダーから取得できない場合のデフォルト名
  const dispositionRaw = res.headers.contentDisposition as unknown;
  if (dispositionRaw && (dispositionRaw as string).includes("attachment")) {
    const disposition = dispositionRaw as string;
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    if (matches?.[1]) {
      filename = matches[1].replace(/['"]/g, "").split("utf-8")[1];
      filename = decodeURIComponent(filename);
    }
  }
  return filename;
};

/**
 * wgs2jgs(世界測地系から日本測地系に変換)
 * 参照: https://qiita.com/takahi/items/85732f577820d8f76b3e
 * @param coordinates 世界測地系の座標
 * @return 日本測地系の座標、leafletのLatLng型
 **/
const wgs2jgs = (coordinates: LatLng): LatLng => {
  proj4.defs([
    [
      "EPSG:4301", // 東京測地系/日本測地系 SRID=4301
      "+proj=longlat +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +no_defs",
    ],
  ]);

  const result = proj4("EPSG:4326", "EPSG:4301", [
    coordinates.lng,
    coordinates.lat,
  ]);
  return new LatLng(result[1], result[0]);
};

/**
 * REGEX_CIRCLESを半角数字の0に変換する。それ以外の文字はそのまま。
 * @param {string} value 変換したい文字列
 * @returns {string} 変換後の文字列
 */
const convertCirclesToZero = (value: string): string => {
  // 一文字ごとに分割して配列に変換。
  const stringArray = [...value];

  // 一文字ずつ実行。特定の丸の文字があれば半角数字の0に変換。
  const result = stringArray.map((c) => c.replace(REGEX_CIRCLES, String(0)));

  // 文字列に戻す。
  return result.join("");
};

/**
 * 値を文字列に正規化する。string はそのまま、number は文字列化、それ以外は空文字を返す。
 * 主に localStorage や外部 API レスポンスのように、型が string と宣言されていても
 * 実体が number で来る可能性がある値の取り扱いに用いる。
 * @param value 任意の値
 * @returns 正規化された文字列
 */
const coerceToString = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return "";
};

/**
 * 緯度・経度の表示形式を整える
 * 6桁を超える場合のみ丸め、6桁以下はそのまま表示
 * @param {number | null | undefined} value 緯度または経度
 * @returns {string} フォーマットされた緯度・経度文字列
 */
const formatLatLng = (value: number | null | undefined): string => {
  // null, undefined, NaNの場合は空文字列を返す
  if (value == null || isNaN(value)) {
    return "";
  }

  // 小数点以下の桁数を確認
  const decimalPart: string | undefined = value.toString().split(".")[1];
  const decimalLength: number = decimalPart ? decimalPart.length : 0;

  // 6桁を超える場合のみ丸める
  if (decimalLength > 6) {
    return value.toFixed(6);
  }

  // 6桁以下の場合はそのまま返す
  return value.toString();
};

export {
  stringBooleanToBoolean,
  camelToSnake,
  numberFormat,
  isSameDate,
  NYHalfWidthNumberToFillWidthNumber,
  NYFillWidthNumberToHalfWidthNumber,
  nationalHolidayCsv,
  preventNonNumericValueWhenPaste,
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWithoutBackspaceWhenKeyDown,
  preventSpaceKey,
  getAttachedFileNameFromHeader,
  wgs2jgs,
  convertCirclesToZero,
  handleCleanArrayValue,
  handleSplitInputValues,
  INPUT_REGEX_SPLIT,
  getInputDelimiterJapanese,
  coerceToString,
  formatLatLng,
};
